import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ClothingItemCard } from "@/components/public/ClothingItemCard";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { MultiSelect } from "@/components/public/MultiSelect";
import { SingleSelect } from "@/components/public/SingleSelect";
import { AdSlot } from "@/components/public/AdSlot";
import { SeoHead } from "@/components/public/SeoHead";
import { useDisplayCurrency } from "@/lib/currency";

export default function ClothingPage() {
  // Allow other pages to deep-link into a pre-filtered view (e.g. the brand
  // detail "See all" link passes `?brand_id=<id>`). Reading once at mount is
  // enough — the user can refine from the sidebar afterwards.
  const [searchParams] = useSearchParams();
  const displayCurrency = useDisplayCurrency();
  const [filters, setFilters] = useState(() => ({
    q: searchParams.get("q") || "",
    brand_id: (searchParams.get("brand_id") || "").split(",").filter(Boolean),
    type_id: (searchParams.get("type_id") || "").split(",").filter(Boolean),
    material_id: (searchParams.get("material_id") || "").split(",").filter(Boolean),
    classification: (searchParams.get("classification") || "").split(",").filter(Boolean),
    flags: (searchParams.get("flags") || "").split(",").filter(Boolean),
    certification_id: (searchParams.get("certification_id") || "").split(",").filter(Boolean),
    sort: searchParams.get("sort") || "newest",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
  }));

  const { data: brands = [] } = useQuery({ queryKey: ["brands-all"], queryFn: async () => (await api.get("/public/brands")).data });
  const { data: types = [] } = useQuery({ queryKey: ["types"], queryFn: async () => (await api.get("/public/clothing-types")).data });
  const { data: materialsGroup } = useQuery({ queryKey: ["materials-all"], queryFn: async () => (await api.get("/public/materials")).data });
  const { data: certifications = [] } = useQuery({ queryKey: ["certifications-all"], queryFn: async () => (await api.get("/public/materials-certifications")).data });
  const allMaterials = useMemo(() => [...(materialsGroup?.GREEN || []), ...(materialsGroup?.YELLOW || []), ...(materialsGroup?.RED || [])], [materialsGroup]);

  const paramsKey = useMemo(() => JSON.stringify({ ...filters, displayCurrency }), [filters, displayCurrency]);
  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length > 0) p[k] = v.join(",");
      } else if (v !== "" && v !== null && v !== undefined) {
        p[k] = v;
      }
    });
    // Tell the backend which currency min_price/max_price are denominated in
    // so it can convert each item's native price for comparison.
    if (p.min_price || p.max_price) p.price_currency = displayCurrency;
    return p;
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["clothing", paramsKey],
    queryFn: async () => (await api.get("/public/clothing", { params })).data,
  });

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  // Client-side pagination — 16 items per page, ad slot every 8 cards.
  const PAGE_SIZE = 16;
  const AD_EVERY = 8;
  const [page, setPage] = useState(1);
  // Reset to page 1 whenever the filter set changes. React docs pattern for
  // "Adjusting state on prop changes" — cheaper than useEffect and runs
  // synchronously during render.
  const [prevFilterKey, setPrevFilterKey] = useState(paramsKey);
  if (prevFilterKey !== paramsKey) {
    setPrevFilterKey(paramsKey);
    setPage(1);
  }
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [items, safePage]
  );
  const goToPage = (n) => {
    setPage(n);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div>
      <SeoHead title="Clothing — Sustainably Curated" description="Editorially reviewed clothing items, classified by material composition: GREEN, YELLOW, or RED — with synthetic content capped at 5%." path="/clothing" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Clothing</h1>
        <p className="text-muted_text">Editorially reviewed clothing. Every item is classified by its composition. Clothing with YELLOW and RED materials are individually reviewed, and accepted depending on the context. RED materials content is capped at 5% per item — see the <Link to="/materials-guide" className="underline underline-offset-4">Materials Guide</Link>.</p>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filter sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-5" data-testid="clothing-filters">
          <input
            value={filters.q}
            onChange={(e) => set("q")(e.target.value)}
            placeholder="Search…"
            className="w-full px-4 py-2.5 rounded-full border border-line bg-white text-sm"
            data-testid="clothing-search-input"
          />

          <FilterBlock label="Sort by">
            <SingleSelect
              testId="clothing-sort-select"
              value={filters.sort}
              onChange={(v) => set("sort")(v)}
              options={[
                { id: "newest", label: "Newest reviewed" },
                { id: "brand", label: "Brand A\u2013Z" },
                { id: "type", label: "Type" },
                { id: "price_asc", label: "Price \u2191" },
                { id: "price_desc", label: "Price \u2193" },
              ]}
            />
          </FilterBlock>

          <FilterBlock label="Brand">
            <MultiSelect
              testId="clothing-brand-select"
              placeholder="All brands"
              options={brands.map((b) => ({ id: b.id, label: b.name }))}
              value={filters.brand_id}
              onChange={(next) => set("brand_id")(next)}
            />
          </FilterBlock>

          <FilterBlock label="Clothing type">
            <MultiSelect
              testId="clothing-type-select"
              placeholder="All types"
              options={types.map((t) => ({ id: t.id, label: t.name }))}
              value={filters.type_id}
              onChange={(next) => set("type_id")(next)}
            />
          </FilterBlock>

          <FilterBlock label="Material">
            <MultiSelect
              testId="clothing-material-select"
              placeholder="All materials"
              options={allMaterials.map((m) => ({
                id: m.id,
                label: m.name,
                className:
                  m.classification === "GREEN" ? "bg-[#e1fce3] hover:bg-[#c5f5c8]"
                  : m.classification === "YELLOW" ? "bg-[#fcf2d9] hover:bg-[#f6e6b3]"
                  : m.classification === "RED" ? "bg-[#fad5d2] hover:bg-[#f5b6b0]"
                  : "hover:bg-sand/60",
              }))}
              value={filters.material_id}
              onChange={(next) => set("material_id")(next)}
            />
          </FilterBlock>

          <FilterBlock label="Clothing Classification">
            <div className="flex flex-wrap gap-2" data-testid="clothing-classification-group">
              {["GREEN", "YELLOW", "RED"].map((c) => {
                const active = filters.classification.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setFilters((f) => ({
                        ...f,
                        classification: active
                          ? f.classification.filter((x) => x !== c)
                          : [...f.classification, c],
                      }))
                    }
                    aria-pressed={active}
                    className={active ? "ring-2 ring-forest rounded-full" : "opacity-60 hover:opacity-100 transition-opacity"}
                    data-testid={`clothing-classification-${c.toLowerCase()}`}
                  >
                    <ClassificationBadge classification={c} />
                  </button>
                );
              })}
            </div>
          </FilterBlock>

          <FilterBlock label="Materials Certifications">
            <MultiSelect
              testId="clothing-certification-select"
              placeholder="Any certification"
              options={certifications.map((c) => ({ id: c.id, label: c.name }))}
              value={filters.certification_id}
              onChange={(next) => set("certification_id")(next)}
            />
          </FilterBlock>

          <FilterBlock label="Editorial flags">
            <div className="flex flex-col gap-1.5" data-testid="clothing-flags-group">
              {[
                { key: "label_verified", label: "Label verified only", testid: "flag-label-verified" },
                { key: "exclude_disputed", label: "Exclude disputed materials", testid: "flag-exclude-disputed" },
                { key: "exclude_mislabeled", label: "Exclude mislabeled materials", testid: "flag-exclude-mislabeled" },
              ].map((opt) => {
                const active = filters.flags.includes(opt.key);
                return (
                  <label
                    key={opt.key}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    data-testid={opt.testid}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() =>
                        setFilters((f) => ({
                          ...f,
                          flags: active
                            ? f.flags.filter((x) => x !== opt.key)
                            : [...f.flags, opt.key],
                        }))
                      }
                      className="accent-forest"
                    />
                    <span className={active ? "text-ink" : "text-muted_text"}>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </FilterBlock>

          <FilterBlock label={`Price (${displayCurrency})`}>
            <div className="flex items-center gap-2">
              <input type="number" placeholder="Min" value={filters.min_price} onChange={(e) => set("min_price")(e.target.value)} className="w-1/2 px-3 py-2 rounded-lg border border-line text-sm" data-testid="clothing-min-price" />
              <input type="number" placeholder="Max" value={filters.max_price} onChange={(e) => set("max_price")(e.target.value)} className="w-1/2 px-3 py-2 rounded-lg border border-line text-sm" data-testid="clothing-max-price" />
            </div>
          </FilterBlock>
        </aside>

        {/* Results */}
        <div>
          {isLoading ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{Array(10).fill(0).map((_, i) => <div key={i} className="h-80 bg-champagne/30 rounded-2xl animate-pulse" />)}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted_text" data-testid="clothing-empty">No items match those filters.</div>
          ) : (
            <div className="space-y-6" data-testid="clothing-grid">
              {Array.from({ length: Math.ceil(pageItems.length / AD_EVERY) }).map((_, chunkIdx) => {
                const chunk = pageItems.slice(chunkIdx * AD_EVERY, (chunkIdx + 1) * AD_EVERY);
                return (
                  <div key={chunkIdx} className="space-y-6">
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {chunk.map((it) => <ClothingItemCard key={it.id} item={it} />)}
                    </div>
                    <AdSlot slotKey="clothing_inline" />
                  </div>
                );
              })}

              {totalPages > 1 && (
                <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
              )}
            </div>
          )}
        </div>
      </div>
      </section>
    </div>
  );
}

function FilterBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted_text mb-2">{label}</div>
      {children}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  // Render a compact set of page buttons. For small totals show every page;
  // for larger sets collapse the middle with an ellipsis.
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    return Array.from(set).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);
  const btn = "min-w-[36px] h-9 px-3 rounded-full text-sm border transition-colors";
  return (
    <nav
      className="flex items-center justify-center gap-1.5 pt-2"
      aria-label="Pagination"
      data-testid="clothing-pagination"
    >
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className={`${btn} border-line bg-white hover:bg-champagne/40 disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid="clothing-page-prev"
        aria-label="Previous page"
      >
        ‹
      </button>
      {pages.map((p, i) => {
        const showEllipsisBefore = i > 0 && p - pages[i - 1] > 1;
        return (
          <span key={p} className="flex items-center gap-1.5">
            {showEllipsisBefore && <span className="px-1 text-muted_text">…</span>}
            <button
              type="button"
              onClick={() => onChange(p)}
              aria-current={p === page ? "page" : undefined}
              className={`${btn} ${p === page ? "bg-forest text-ivory border-forest" : "border-line bg-white hover:bg-champagne/40"}`}
              data-testid={`clothing-page-${p}`}
            >
              {p}
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className={`${btn} border-line bg-white hover:bg-champagne/40 disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid="clothing-page-next"
        aria-label="Next page"
      >
        ›
      </button>
    </nav>
  );
}
